import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fileUrl } from "@/lib/api";
import { DataManager } from "@/components/DataManager";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Receipt, Search, Eye } from "lucide-react";

const EXPENSE_CATEGORIES = [
  "Rent / Lease", "Electricity", "Gas", "Water / Sewer", "Internet", "Phone / VOIP",
  "Trash / Waste Disposal", "Cleaning Service", "Insurance", "Software / SaaS",
  "Payroll", "Payroll Taxes", "Bank / Merchant Fees", "Accounting / Legal",
  "Marketing", "Cleanroom Certification Fees", "Regulatory Fees", "Miscellaneous"
];

const fields = [
  { name: "expense_date", label: "Date", type: "date", required: true },
  { name: "category", label: "Category", type: "select", options: EXPENSE_CATEGORIES, required: true },
  { name: "vendor", label: "Vendor / Payee", required: true },
  { name: "amount", label: "Amount (USD)", type: "number", required: true },
  { name: "recurrence", label: "Recurrence", type: "select", options: ["one-time", "monthly", "quarterly", "annual"], default: "monthly" },
  { name: "due_day", label: "Due Day of Month (if recurring)", type: "number" },
  { name: "payment_method", label: "Payment Method", type: "select", options: ["ACH", "Credit Card", "Debit Card", "Check", "Wire", "Cash", "Other"] },
  { name: "status", label: "Status", type: "select", options: ["paid", "pending", "overdue"], default: "paid" },
  { name: "reference", label: "Reference / Account #", mono: true },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "receipt_file", label: "Attach Receipt / Bill (PDF or image)", type: "file", full: true },
];

const money = (n) => `$${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");

  const reload = useCallback(() => api.get("/expenses").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((r) => {
      if (catFilter && r.category !== catFilter) return false;
      if (!needle) return true;
      return [r.vendor, r.category, r.reference, r.notes].some((v) => String(v || "").toLowerCase().includes(needle));
    });
  }, [items, q, catFilter]);

  const totals = useMemo(() => {
    const now = new Date();
    const thisMonth = filtered.filter((r) => {
      if (!r.expense_date) return false;
      const d = new Date(r.expense_date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    const sum = (arr) => arr.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const monthlyRecurring = items.filter((r) => r.recurrence === "monthly");
    return {
      thisMonth: sum(thisMonth),
      recurringMonthly: sum(monthlyRecurring),
      annualRecurringProjection: sum(monthlyRecurring) * 12,
    };
  }, [filtered, items]);

  const columns = [
    { name: "expense_date", label: "Date", mono: true },
    { name: "vendor", label: "Vendor / Payee", render: (r) => (
      <div>
        <div className="font-medium text-slate-800">{r.vendor}</div>
        {r.reference && <div className="text-xs text-slate-400 font-mono">{r.reference}</div>}
      </div>
    ) },
    { name: "category", label: "Category" },
    { name: "recurrence", label: "Cycle", render: (r) => <span className="capitalize text-xs">{r.recurrence || "—"}</span> },
    { name: "amount", label: "Amount", render: (r) => <span className="font-mono font-semibold">{money(r.amount)}</span> },
    { name: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { name: "receipt_file", label: "Receipt", render: (r) => (
      r.receipt_file
        ? <Button data-testid={`expense-view-${r.id}`} variant="ghost" size="sm" className="h-8 gap-1 text-pink-800"
            onClick={() => window.open(fileUrl(r.receipt_file), "_blank")}><Eye className="w-3.5 h-3.5" /> View</Button>
        : <span className="text-slate-300">—</span>
    ) },
  ];

  return (
    <div>
      <PageHeader testid="expenses-header" title="Pharmacy Expenses"
        subtitle="Track rent, utilities, phone, internet and everything else that isn't a supplier invoice." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <Card className="p-4 border-slate-200">
          <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">This month</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1 font-mono">{money(totals.thisMonth)}</div>
        </Card>
        <Card className="p-4 border-slate-200">
          <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">Monthly recurring</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1 font-mono">{money(totals.recurringMonthly)}</div>
        </Card>
        <Card className="p-4 border-slate-200">
          <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">Annual projection</div>
          <div className="font-heading text-2xl font-bold text-pink-800 mt-1 font-mono">{money(totals.annualRecurringProjection)}</div>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="expense-search" placeholder="Search vendor, category, reference…" value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" />
        </div>
        <select data-testid="expense-category-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="">All categories</option>
          {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <DataManager testidPrefix="expense" endpoint="expenses" fields={fields} columns={columns}
        items={filtered} reload={reload} emptyIcon={Receipt}
        emptyTitle={q || catFilter ? "No expenses match your filter" : "No expenses tracked yet"} />
    </div>
  );
}
